import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BadgePercent, Plus, Pencil, Pause, Play, Trash2, BarChart3, Loader2,
  MapPin, Upload, X, Mail, ExternalLink, LogOut, Users, MousePointerClick, Store,
  Rocket, Zap, TrendingUp, Layers, Clock, ChevronDown, ChevronUp, FileText, Save, Check
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("token");
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
const CATS = ["Commercio", "Cibo e bere", "Evento", "Divertimento", "Avventura", "Trasporti", "Servizi alla persona", "Deposito bagagli", "Altro"];

const empty = { business_name: "", category: "Commercio", business_desc: "", promo_desc: "", discount_pct: "", discount_label: "", activation_type: "link", link_url: "", code_text: "", gmaps_url: "", logo_url: "", istat: "", city_nome: "", city_provincia: "" };

function CitySearch({ value, onSelect }) {
  const [q, setQ] = useState(value || "");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { setQ(value || ""); }, [value]);
  const search = async (val) => {
    setQ(val); onSelect({ istat: "", city_nome: "", city_provincia: "" });
    if (val.length < 2) { setRes([]); return; }
    try {
      const r = await axios.get(`${API}/comuni/search?q=${encodeURIComponent(val)}&limit=8`);
      setRes(r.data.results || []); setOpen(true);
    } catch { setRes([]); }
  };
  return (
    <div className="relative">
      <input value={q} onChange={(e) => search(e.target.value)} placeholder="Città di riferimento (ISTAT)" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-city-input" />
      {open && res.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-zinc-200 shadow-xl max-h-56 overflow-y-auto">
          {res.map((c) => (
            <button key={c.istat} type="button" onClick={() => { onSelect({ istat: c.istat, city_nome: c.nome, city_provincia: c.provincia }); setQ(`${c.nome} (${c.sigla})`); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2" data-testid={`pro-city-opt-${c.istat}`}>
              <MapPin className="w-3.5 h-3.5 text-zinc-400" /> {c.nome} <span className="text-zinc-400">({c.provincia})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PromoForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    const merged = { ...empty, ...(initial || {}) };
    Object.keys(merged).forEach((k) => { if (merged[k] == null) merged[k] = empty[k] ?? ""; });
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await axios.post(`${API}/pro/upload`, fd, auth()); setF((s) => ({ ...s, logo_url: r.data.url })); }
    catch { setErr("Upload logo fallito"); }
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      if (initial?.promo_id) await axios.put(`${API}/pro/promos/${initial.promo_id}`, f, auth());
      else await axios.post(`${API}/pro/promos`, f, auth());
      onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pro-promo-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black">{initial?.promo_id ? "Modifica promo" : "Nuova promozione"}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center overflow-hidden cursor-pointer shrink-0" data-testid="pro-logo-upload">
              {f.logo_url ? <img src={f.logo_url.startsWith("http") ? f.logo_url : `${process.env.REACT_APP_BACKEND_URL}${f.logo_url}`} alt="" className="w-full h-full object-cover" /> : <Upload className="w-5 h-5 text-zinc-400" />}
              <input type="file" accept="image/*" className="hidden" onChange={upload} />
            </label>
            <input value={f.business_name} onChange={set("business_name")} placeholder="Nome attività" className="flex-1 rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-name" />
          </div>
          <CitySearch value={f.city_nome ? `${f.city_nome}` : ""} onSelect={(c) => setF((s) => ({ ...s, ...c }))} />
          <select value={f.category} onChange={set("category")} className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-category">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea value={f.business_desc} onChange={set("business_desc")} placeholder="Descrizione azienda" rows={2} className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-bizdesc" />
          <textarea value={f.promo_desc} onChange={set("promo_desc")} placeholder="Descrizione della promo" rows={2} className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-promodesc" />
          <div className="grid grid-cols-2 gap-3">
            <input value={f.discount_pct} onChange={set("discount_pct")} placeholder="% sconto (es. 15)" className="rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-pct" />
            <input value={f.discount_label} onChange={set("discount_label")} placeholder="oppure etichetta" className="rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-label" />
          </div>
          <div className="flex gap-2">
            {["link", "code"].map((t) => (
              <button key={t} type="button" onClick={() => setF((s) => ({ ...s, activation_type: t }))} className={`flex-1 py-2 rounded-xl text-sm font-bold ${f.activation_type === t ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid={`pro-f-act-${t}`}>
                {t === "link" ? "Link esterno" : "Codice sconto"}
              </button>
            ))}
          </div>
          {f.activation_type === "link"
            ? <input value={f.link_url} onChange={set("link_url")} placeholder="https://..." className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-link" />
            : <input value={f.code_text} onChange={set("code_text")} placeholder="Codice / frase / parola" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-code" />}
          <div>
            <input value={f.gmaps_url} onChange={set("gmaps_url")} placeholder="Link Google Maps (obbligatorio)" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-f-gmaps" />
            <p className="text-[11px] text-zinc-400 mt-1 px-1">Obbligatorio: posiziona lo sconto sulla mappa. Apri l'attività su Google Maps → Condividi → copia il link.</p>
          </div>
          {err && <p className="text-sm text-rose-600" data-testid="pro-f-error">{err}</p>}
          <button onClick={save} disabled={saving} className="w-full py-3 rounded-full bg-zinc-900 text-white font-black text-sm disabled:opacity-60" data-testid="pro-f-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Salva promozione"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const daysLeft = (until) => {
  if (!until) return 0;
  const ms = new Date(until).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 86400000) : 0;
};

function MissileBadge({ boost }) {
  if (!boost) return null;
  const sp = boost === "super_power";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-black text-white shrink-0 ${sp ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]"}`}
      style={{ background: sp ? "linear-gradient(135deg,#7C3AED,#a855f7)" : "linear-gradient(135deg,#ef4444,#dc2626)" }}
      data-testid={`boost-badge-${boost}`}>
      <Rocket className={sp ? "w-4 h-4" : "w-3 h-3"} /> {sp ? "SUPER POWER" : "POWER"}
    </span>
  );
}

function BoostButtons({ promo, onBuy, busy }) {
  const t = promo.boost;
  const pLeft = daysLeft(promo.power_until);
  const spLeft = daysLeft(promo.super_power_until);
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={() => onBuy(promo.promo_id, "power")} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 transition-colors disabled:opacity-50"
        style={{ borderColor: "#ef4444", color: t === "power" ? "#fff" : "#ef4444", background: (t === "power") ? "#ef4444" : "transparent" }}
        data-testid={`buy-power-${promo.promo_id}`}>
        <Rocket className="w-3.5 h-3.5" /> {pLeft > 0 ? `Power · ancora ${pLeft}gg` : "Power · 2,99€/mese"}
      </button>
      <button onClick={() => onBuy(promo.promo_id, "super_power")} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 transition-colors disabled:opacity-50"
        style={{ borderColor: "#7C3AED", color: t === "super_power" ? "#fff" : "#7C3AED", background: (t === "super_power") ? "#7C3AED" : "transparent" }}
        data-testid={`buy-superpower-${promo.promo_id}`}>
        <Rocket className="w-4 h-4" /> {spLeft > 0 ? `Super Power · ancora ${spLeft}gg` : "Super Power · 9,99€/anno"}
      </button>
    </div>
  );
}

function PromoAnalytics({ promoId }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pro/promos/${promoId}/analytics?days=30`, auth()).then((r) => setA(r.data)).catch(() => setA({ error: true }));
  }, [promoId]);
  if (!a) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-300 mx-auto" /></div>;
  if (a.error) return <p className="text-xs text-zinc-400 text-center py-6">Analytics non disponibili.</p>;
  const tiles = [
    { i: Users, n: a.totals.active_hosts, l: "Welcome book attivi ora", c: "text-violet-600 bg-violet-50" },
    { i: Layers, n: a.totals.activations_period, l: "Pubblicazioni (30gg)", c: "text-sky-600 bg-sky-50" },
    { i: MousePointerClick, n: a.totals.clicks_period, l: "Click ospiti (30gg)", c: "text-rose-600 bg-rose-50" },
    { i: TrendingUp, n: a.totals.clicks, l: "Click totali", c: "text-emerald-600 bg-emerald-50" },
  ];
  return (
    <div className="space-y-4" data-testid={`promo-analytics-${promoId}`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {tiles.map((t) => (
          <div key={t.l} className="rounded-2xl border border-zinc-100 p-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${t.c}`}><t.i className="w-4 h-4" /></div>
            <div className="text-2xl font-black tabular-nums leading-none">{t.n}</div>
            <div className="text-[10px] text-zinc-500 font-semibold mt-1">{t.l}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-zinc-100 p-3">
        <p className="text-[11px] font-black uppercase tracking-wide text-zinc-400 mb-2 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> Ultimi 30 giorni</p>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={a.series} margin={{ top: 4, right: 6, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gAct" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7C3AED" stopOpacity={0.35} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0} /></linearGradient>
              <linearGradient id="gClk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} /><stop offset="100%" stopColor="#ef4444" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10, fill: "#a1a1aa" }} interval={4} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12 }} labelFormatter={(d) => new Date(d).toLocaleDateString("it-IT")} />
            <Area type="monotone" dataKey="activations" name="Pubblicazioni" stroke="#7C3AED" strokeWidth={2} fill="url(#gAct)" />
            <Area type="monotone" dataKey="clicks" name="Click" stroke="#ef4444" strokeWidth={2} fill="url(#gClk)" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-4 mt-1 text-[11px] font-bold">
          <span className="flex items-center gap-1.5 text-violet-600"><span className="w-2.5 h-2.5 rounded-full bg-violet-600" /> Pubblicazioni</span>
          <span className="flex items-center gap-1.5 text-rose-500"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Click ospiti</span>
        </div>
      </div>
    </div>
  );
}

export default function ProDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [social, setSocial] = useState(null);
  const [busyBoost, setBusyBoost] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [fiscal, setFiscal] = useState(null);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [fiscalOk, setFiscalOk] = useState(false);
  const [fiscalErr, setFiscalErr] = useState("");

  useEffect(() => { axios.get(`${API}/pro/social`).then((r) => setSocial(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    if (!token()) { navigate("/pro/register"); return; }
    try {
      const r = await axios.get(`${API}/pro/me`, auth());
      setData(r.data);
      const p = r.data.profile || {};
      setFiscal({ business_name: p.business_name || "", tax_id: p.tax_id || "", billing_address: p.billing_address || "", billing_city: p.billing_city || "", billing_province: p.billing_province || "", billing_cap: p.billing_cap || "", sdi_pec: p.sdi_pec || "" });
    } catch (e) {
      if (e?.response?.status === 403) navigate("/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    // Return from Stripe checkout (abbonamento o boost one-shot)
    const params = new URLSearchParams(window.location.search);
    if (params.get("pro_paid") && params.get("session_id")) {
      axios.post(`${API}/pro/subscribe/confirm`, { session_id: params.get("session_id") }, auth())
        .finally(() => { window.history.replaceState({}, "", "/pro/dashboard"); load(); });
    } else if (params.get("boost_paid") && params.get("session_id")) {
      axios.post(`${API}/pro/boost/confirm`, { session_id: params.get("session_id") }, auth())
        .finally(() => { window.history.replaceState({}, "", "/pro/dashboard"); load(); });
    } else { load(); }
  }, [load]);

  const subscribe = async () => {
    const r = await axios.post(`${API}/pro/subscribe/checkout`, { origin_url: window.location.origin }, auth());
    window.location.href = r.data.url;
  };
  const buyBoost = async (promoId, tier) => {
    setBusyBoost(true);
    try {
      const r = await axios.post(`${API}/pro/promos/${promoId}/boost/checkout`, { tier, origin_url: window.location.origin }, auth());
      window.location.href = r.data.url;
    } catch { setBusyBoost(false); }
  };
  const togglePause = async (id) => { await axios.post(`${API}/pro/promos/${id}/pause`, {}, auth()); load(); };
  const del = async (id) => { if (window.confirm("Eliminare la promo?")) { await axios.delete(`${API}/pro/promos/${id}`, auth()); load(); } };
  const delAccount = async () => { if (window.confirm("Eliminare l'account e tutte le promo? Azione irreversibile.")) { await axios.delete(`${API}/pro/me/account`, auth()); localStorage.removeItem("token"); navigate("/pro"); } };
  const setF = (k) => (e) => { setFiscal((f) => ({ ...f, [k]: e.target.value })); setFiscalOk(false); setFiscalErr(""); };
  const saveFiscal = async () => {
    setSavingFiscal(true); setFiscalErr(""); setFiscalOk(false);
    try {
      await axios.put(`${API}/pro/me/profile`, fiscal, auth());
      setFiscalOk(true); load();
    } catch (e) { setFiscalErr(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSavingFiscal(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  if (!data) return null;

  const active = data.subscription?.status === "active";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="pro-dashboard">
      <header className="bg-zinc-900 text-white px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BadgePercent className="w-5 h-5" />
            <span className="font-black">{data.profile.business_name}</span>
            <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Hostland Pro</span>
          </div>
          <button onClick={() => { localStorage.removeItem("token"); navigate("/pro"); }} className="text-white/70 hover:text-white text-sm flex items-center gap-1"><LogOut className="w-4 h-4" /> Esci</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Istruzioni + host iscritti */}
        <div className="rounded-3xl bg-white border border-zinc-100 p-5" data-testid="pro-instructions">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h2 className="font-black text-lg">Come funziona</h2>
            {social && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 font-black text-sm"><Users className="w-4 h-4" /> {social.hosts} host su Hostland</span>}
          </div>
          <ol className="grid sm:grid-cols-3 gap-3 text-sm">
            {[["1", "Attiva l'abbonamento", "Il primo annuncio si sblocca pagando 19,90€/anno con Stripe."], ["2", "Crea le tue promo", "Promo illimitate: logo, sconto, città, link o codice."], ["3", "Raggiungi gli ospiti", "Gli host della tua zona attivano le tue promo nel welcome book."]].map(([n, t2, d]) => (
              <li key={n} className="rounded-2xl bg-zinc-50 p-3">
                <span className="w-7 h-7 rounded-full bg-zinc-900 text-white font-black inline-flex items-center justify-center text-xs mb-2">{n}</span>
                <p className="font-black">{t2}</p><p className="text-xs text-zinc-500 mt-0.5">{d}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Come vengono mostrate le promo + Power/Super Power */}
        <div className="rounded-3xl border border-zinc-100 bg-white p-5" data-testid="pro-power-info">
          <h2 className="font-black text-lg mb-1 flex items-center gap-2"><Layers className="w-5 h-5 text-violet-600" /> Come vengono mostrate le tue promo</h2>
          <p className="text-sm text-zinc-600 leading-relaxed mb-4">
            Nel Welcome Book le promo sono <strong>divise per categoria</strong> e, all'interno di ogni categoria, mostrate <strong>in ordine di pubblicazione</strong>.
            Vuoi stare più in alto e farti notare? Attiva un <strong>boost</strong>: i boost restano sempre sopra le promo normali della stessa categoria.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl p-4 border-2" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
              <div className="flex items-center gap-2 mb-1"><span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)" }}><Rocket className="w-4 h-4" /></span><span className="font-black text-rose-700">Power</span><span className="ml-auto font-black text-rose-700">2,99€</span></div>
              <p className="text-xs text-zinc-600">In cima alla tua categoria per <strong>1 mese</strong>. Pagamento singolo (no abbonamento). Più power nella stessa categoria? Restano sopra, in ordine di pubblicazione.</p>
            </div>
            <div className="rounded-2xl p-4 border-2" style={{ borderColor: "#ddd6fe", background: "#f5f3ff" }}>
              <div className="flex items-center gap-2 mb-1"><span className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#7C3AED,#a855f7)" }}><Rocket className="w-5 h-5" /></span><span className="font-black text-violet-700">Super Power</span><span className="ml-auto font-black text-violet-700">9,99€</span></div>
              <p className="text-xs text-zinc-600">Sempre <strong>sopra le Power</strong> per <strong>1 anno</strong>. Pagamento singolo. Più super power? In ordine di pubblicazione, ma sempre in cima a tutto.</p>
            </div>
          </div>
        </div>

        {/* Subscription */}
        {!active ? (
          <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white p-6" data-testid="pro-sub-gate">
            <h2 className="text-xl font-black mb-1">Attiva il tuo abbonamento</h2>
            <p className="text-white/80 text-sm mb-4">19,90€/anno per pubblicare le tue promozioni e raggiungere gli host della tua zona.</p>
            <button onClick={subscribe} className="px-6 py-3 rounded-full bg-white text-zinc-900 font-black text-sm" data-testid="pro-subscribe-btn">Abbonati ora · 19,90€/anno</button>
          </div>
        ) : (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-3 flex items-center justify-between" data-testid="pro-sub-active">
            <span className="text-sm font-bold text-emerald-800">✓ Abbonamento attivo</span>
            {data.subscription.current_period_end && <span className="text-xs text-emerald-600">Rinnovo: {new Date(data.subscription.current_period_end).toLocaleDateString("it-IT")}</span>}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[{ i: Store, n: data.stats.promo_count, l: "Promo" }, { i: MousePointerClick, n: data.stats.total_clicks, l: "Click" }, { i: Users, n: data.stats.total_active_hosts, l: "Host attivi" }].map((s) => (
            <div key={s.l} className="rounded-2xl bg-white border border-zinc-100 p-4 text-center">
              <s.i className="w-5 h-5 mx-auto mb-1 text-violet-500" />
              <div className="text-2xl font-black tabular-nums">{s.n}</div>
              <div className="text-[11px] text-zinc-500 font-semibold">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Promos */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black">Le tue promozioni</h2>
          <button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!active} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-zinc-900 text-white font-bold text-sm disabled:opacity-40" data-testid="pro-new-promo-btn"><Plus className="w-4 h-4" /> Nuova</button>
        </div>
        <div className="space-y-3">
          {data.promos.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">Nessuna promozione ancora.</p>}
          {data.promos.map((p) => (
            <div key={p.promo_id} className="rounded-2xl bg-white border border-zinc-100 overflow-hidden" data-testid={`pro-promo-${p.promo_id}`}
              style={p.boost ? { borderColor: p.boost === "super_power" ? "#c4b5fd" : "#fca5a5", borderWidth: 2 } : {}}>
              <div className="p-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-zinc-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {p.logo_url ? <img src={p.logo_url.startsWith("http") ? p.logo_url : `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`} alt="" className="w-full h-full object-cover" /> : <Store className="w-6 h-6 text-zinc-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-black truncate">{p.business_name}</p>
                    <MissileBadge boost={p.boost} />
                    {p.status === "paused" && <span className="text-[9px] font-black uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">In pausa</span>}
                  </div>
                  <p className="text-xs text-zinc-500">{p.category} · {p.city?.nome} · {p.clicks} click · {p.active_hosts} host</p>
                  {p.boost && (
                    <p className="text-[11px] font-bold mt-0.5 inline-flex items-center gap-1" style={{ color: p.boost === "super_power" ? "#7C3AED" : "#ef4444" }}>
                      <Clock className="w-3 h-3" /> Boost attivo · scade tra {daysLeft(p.boost === "super_power" ? p.super_power_until : p.power_until)} giorni
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setExpanded(expanded === p.promo_id ? null : p.promo_id)} className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center" title="Statistiche" data-testid={`pro-stats-${p.promo_id}`}>{expanded === p.promo_id ? <ChevronUp className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}</button>
                  <button onClick={() => { setEditing(p); setShowForm(true); }} className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center" title="Modifica" data-testid={`pro-edit-${p.promo_id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => togglePause(p.promo_id)} className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center" title="Pausa" data-testid={`pro-pause-${p.promo_id}`}>{p.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
                  <button onClick={() => del(p.promo_id)} className="w-9 h-9 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" title="Elimina" data-testid={`pro-del-${p.promo_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="px-4 pb-4">
                <BoostButtons promo={p} onBuy={buyBoost} busy={busyBoost} />
              </div>
              <AnimatePresence>
                {expanded === p.promo_id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-zinc-100 bg-zinc-50/50">
                    <div className="p-4"><PromoAnalytics promoId={p.promo_id} /></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Dati fiscali / fatturazione */}
        {fiscal && (
          <div className="rounded-2xl bg-white border border-zinc-100 p-5 space-y-3" data-testid="pro-fiscal-section">
            <h3 className="font-black text-sm uppercase tracking-widest text-zinc-400 flex items-center gap-2"><FileText className="w-4 h-4" /> Dati fiscali &amp; fatturazione</h3>
            <input value={fiscal.business_name} onChange={setF("business_name")} placeholder="Nome attività / ragione sociale" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-business" />
            <input value={fiscal.tax_id} onChange={setF("tax_id")} placeholder="Partita IVA o Codice Fiscale" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-taxid" />
            <input value={fiscal.billing_address} onChange={setF("billing_address")} placeholder="Indirizzo fiscale (via e n. civico)" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-address" />
            <div className="grid grid-cols-[1fr_auto_auto] gap-2">
              <input value={fiscal.billing_city} onChange={setF("billing_city")} placeholder="Città" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-city" />
              <input value={fiscal.billing_province} onChange={setF("billing_province")} maxLength={2} placeholder="Prov." className="w-16 rounded-xl border border-zinc-200 px-3 py-2.5 text-sm uppercase outline-none focus:border-zinc-900" data-testid="pro-fiscal-province" />
              <input value={fiscal.billing_cap} onChange={setF("billing_cap")} maxLength={5} placeholder="CAP" className="w-24 rounded-xl border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-cap" />
            </div>
            <input value={fiscal.sdi_pec} onChange={setF("sdi_pec")} placeholder="Codice destinatario SDI (7 caratteri) o PEC" className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-zinc-900" data-testid="pro-fiscal-sdi" />
            {fiscalErr && <p className="text-sm text-rose-600 font-medium" data-testid="pro-fiscal-error">{fiscalErr}</p>}
            <div className="flex items-center gap-3">
              <button onClick={saveFiscal} disabled={savingFiscal} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 text-white font-bold text-sm disabled:opacity-50" data-testid="pro-fiscal-save">
                {savingFiscal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva dati fiscali
              </button>
              {fiscalOk && <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm" data-testid="pro-fiscal-ok"><Check className="w-4 h-4" /> Salvato</span>}
            </div>
          </div>
        )}

        {/* Contacts + account */}
        <div className="rounded-2xl bg-white border border-zinc-100 p-5 space-y-3">
          <h3 className="font-black text-sm uppercase tracking-widest text-zinc-400">Contatti & Account</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { window.location.href = "mailto:vitadahost@gmail.com?subject=Hostland%20Pro"; }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-100 font-bold text-sm" data-testid="pro-contact-email"><Mail className="w-4 h-4" /> Contattaci</button>
            <a href="https://www.vitadahost.it" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-100 font-bold text-sm"><ExternalLink className="w-4 h-4" /> Vita da Host</a>
            <button onClick={delAccount} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 text-rose-600 font-bold text-sm ml-auto" data-testid="pro-delete-account"><Trash2 className="w-4 h-4" /> Elimina account</button>
          </div>
        </div>
      </main>

      <AnimatePresence>
        {showForm && <PromoForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
}
